/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { ReactNode } from "react";

import MuiCheckbox, {
  CheckboxProps as MuiCheckboxProps
} from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";

import { colors } from "@dotnet/telematics-design-system";

import CheckboxDefaultCheckedIcon from "../../assets/customIcons/checkbox/CheckboxDefaultCheckedIcon";
import CheckboxDefaultIcon from "../../assets/customIcons/checkbox/CheckboxDefaultIcon";
import CheckboxDefaultIndeterminateIcon from "../../assets/customIcons/checkbox/CheckboxDefaultIndeterminateIcon";
import CheckboxDisabledCheckedIcon from "../../assets/customIcons/checkbox/CheckboxDisabledCheckedIcon";
import CheckboxDisabledIcon from "../../assets/customIcons/checkbox/CheckboxDisabledIcon";
import CheckboxDisabledIndeterminateIcon from "../../assets/customIcons/checkbox/CheckboxDisabledIndeterminateIcon";
import CheckboxErrorCheckedIcon from "../../assets/customIcons/checkbox/CheckboxErrorCheckedIcon";
import CheckboxErrorIcon from "../../assets/customIcons/checkbox/CheckboxErrorIcon";
import CheckboxErrorIndeterminateIcon from "../../assets/customIcons/checkbox/CheckboxErrorIndeterminateIcon";
import { useStyles as sharedStyles } from "../../shared/styles/sharedStyles";
import InfoIconWithTooltip from "../IconsWithTooltips/InfoIconWithTooltip/InfoIconWithTooltip";
import OptionalTooltip from "../Tooltip/OptionalTooltip";
import { useStyles } from "./styles";

export interface ICheckboxProps {
  label?: string | ReactNode;
  customLabel?: string | ReactNode;
  info?: string;
  isError?: boolean;
  customStyle?: {};
  customId?: string;
  hasTooltip?: boolean;
  tooltipTitle?: string;
  infoIconTooltip?: string;
  infoIconTooltipSize?: "small" | "medium";
}

const Checkbox = ({
  label,
  customLabel,
  info,
  isError,
  customStyle,
  customId,
  hasTooltip = false,
  tooltipTitle = "",
  infoIconTooltip,
  infoIconTooltipSize,
  ...props
}: ICheckboxProps & MuiCheckboxProps) => {
  const {
    blue700,
    blue800,
    gray300,
    gray400,
    gray700,
    textDark,
    textDisabled
  } = colors;

  const { checkboxContent, checkboxLabel, infoIconCSS } = useStyles();
  const { body3Regular } = sharedStyles();

  const getIcons = () => {
    if (props.disabled) {
      return {
        icon: <CheckboxDisabledIcon />,
        checkedIcon: <CheckboxDisabledCheckedIcon />,
        indeterminateIcon: <CheckboxDisabledIndeterminateIcon />
      };
    } else if (isError) {
      return {
        icon: <CheckboxErrorIcon />,
        checkedIcon: <CheckboxErrorCheckedIcon />,
        indeterminateIcon: <CheckboxErrorIndeterminateIcon />
      };
    } else {
      return {
        icon: <CheckboxDefaultIcon />,
        checkedIcon: <CheckboxDefaultCheckedIcon />,
        indeterminateIcon: <CheckboxDefaultIndeterminateIcon />
      };
    }
  };

  const icon = getIcons().icon;
  const checkedIcon = getIcons().checkedIcon;
  const indeterminateIcon = getIcons().indeterminateIcon;

  const isDefault = !props.disabled && !isError;

  const getHoverColor = () => {
    if (!isDefault) {
      return blue700;
    }

    return props.indeterminate ? blue800 : gray400;
  };

  const hoverColor = getHoverColor();

  return (
    <OptionalTooltip
      isDisabled={!hasTooltip}
      tooltipTitle={tooltipTitle}
      tooltipPlacement="top"
    >
      <label
        css={css([
          checkboxLabel,
          {
            ...customStyle
          }
        ])}
      >
        <MuiCheckbox
          data-testid={`checkbox${customId}`}
          {...props}
          disableRipple
          size="small"
          icon={icon}
          checkedIcon={checkedIcon}
          indeterminateIcon={indeterminateIcon}
          sx={{
            alignSelf: "flex-start",
            padding: "0",
            color: gray300,
            transition: "color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",

            "&.Mui-checked, &.MuiCheckbox-indeterminate": {
              color: blue700
            },

            "& > svg": {
              fontSize: "24px"
            },

            "&:hover": {
              color: hoverColor,

              "&.Mui-checked": {
                color: isDefault ? blue800 : blue700
              }
            }
          }}
        />

        <div
          css={css([
            {
              cursor: props.disabled ? "auto" : "pointer",
              marginLeft: label || info || customLabel ? "8px" : "0"
            },
            checkboxContent
          ])}
        >
          {label && (
            <Typography
              variant="body2Medium"
              sx={{
                color: props.disabled ? textDisabled : textDark,
                marginTop: infoIconTooltip ? "2px" : 0
              }}
            >
              {label}
              {infoIconTooltip && (
                <InfoIconWithTooltip
                  title={infoIconTooltip}
                  iconTestId={`${infoIconTooltip}-info icon`}
                  customIconStyle={infoIconCSS}
                  tooltipSize={infoIconTooltipSize ?? "small"}
                />
              )}
            </Typography>
          )}

          {customLabel && <>{customLabel}</>}

          {info && (
            <Typography
              data-testid="checkbox-info"
              css={css([
                {
                  color: props.disabled ? textDisabled : gray700,
                  marginTop: label ? "0" : "2px"
                },
                body3Regular
              ])}
            >
              {info}
            </Typography>
          )}
        </div>
      </label>
    </OptionalTooltip>
  );
};

export default Checkbox;
